import React, { useEffect, useMemo, useRef, useState } from "react";
import { Auth } from "aws-amplify";
import { useHistory, useLocation } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import "../../auth.css";

const AUTH_STATUS_BASE = `https://${process.env.REACT_APP_ACCOUNT_API_URL}`;
const LS_KEY = "ph_prefill_email";

const MFA_ENROLLMENT_COMPLETE_URL =
  process.env.REACT_APP_MFA_ENROLLMENT_COMPLETE_URL || "";

const MFA_TOTP_RESET_COMPLETE_URL =
  process.env.REACT_APP_MFA_TOTP_RESET_COMPLETE_URL || "";

const ReqItem = ({ ok, children }) => (
  <li className={`ph-req ${ok ? "ok" : "bad"}`}>
    <span aria-hidden="true" style={{ display: "inline-block", width: 18 }}>
      {ok ? "✓" : "✕"}
    </span>
    <span>{children}</span>
  </li>
);

function rememberEmail(key, v) {
  try {
    localStorage.setItem(key, v || "");
  } catch {}
}

function getRecoveryTokenFromLocation(location) {
  const sp = new URLSearchParams(location.search);
  return (sp.get("token") || "").trim();
}

function goForgotCustom(history, email) {
  const trimmedEmail = (email || "").trim();

  rememberEmail(LS_KEY, trimmedEmail);

  const qs = trimmedEmail ? `?email=${encodeURIComponent(trimmedEmail)}` : "";
  history.push(`/auth/mfa/request${qs}`);
}

function goRecoveryCustom(history, email, token = "") {
  const trimmedEmail = (email || "").trim();
  const trimmedToken = (token || "").trim();

  rememberEmail(LS_KEY, trimmedEmail);

  const params = new URLSearchParams();
  if (trimmedEmail) {
    params.set("email", trimmedEmail);
  }
  if (trimmedToken) {
    params.set("token", trimmedToken);
  }

  const qs = params.toString() ? `?${params.toString()}` : "";
  history.push(`/auth/mfa/reset-authenticator${qs}`);
}

function isTotpResetResume(location) {
  const sp = new URLSearchParams(location.search);
  return sp.get("totpReset") === "1";
}

function buildOtpAuthUrl({ secret, email, mfaUser }) {
  const issuer = process.env.REACT_APP_TOTP_ISSUER || "ACCOUNT";
  const labelEmail = (email || mfaUser?.username || "").trim() || "user";
  const label = `${issuer}:${labelEmail}`;

  return (
    "otpauth://totp/" +
    encodeURIComponent(label) +
    `?secret=${secret}` +
    `&issuer=${encodeURIComponent(issuer)}` +
    `&algorithm=SHA1&digits=6&period=30`
  );
}

async function postMfaCompletion(url) {
  if (!url) {
    throw new Error(
      "Authenticator app setup completed, but the completion endpoint is not configured."
    );
  }

  const session = await Auth.currentSession();
  const idToken = session.getIdToken().getJwtToken();

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!res.ok) {
    let msg =
      "Authenticator app setup completed, but completion status could not be saved.";

    try {
      const data = await res.json();
      if (data?.message) {
        msg = data.message;
      }
    } catch {}

    throw new Error(msg);
  }
}

async function markMfaEnrollmentCompleted() {
  await postMfaCompletion(MFA_ENROLLMENT_COMPLETE_URL);
}

async function markTotpResetCompleted() {
  await postMfaCompletion(MFA_TOTP_RESET_COMPLETE_URL);
}

async function beginMfaSetupFromFreshUser({
  user,
  email,
  setBusy,
  setError,
  setMfaUser,
  setOtpAuthUrl,
  setStage,
}) {
  if (!user) return;

  try {
    setBusy(true);
    setError("");

    const secret = await Auth.setupTOTP(user);
    const otpAuthUrl = buildOtpAuthUrl({ secret, email, mfaUser: user });

    setMfaUser(user);
    setOtpAuthUrl(otpAuthUrl);
    setStage("MFA_SETUP");
  } catch (e) {
    console.error("Error starting MFA TOTP setup", e);
    setError(e?.message || "We couldn’t start MFA setup. Please try again.");
  } finally {
    setBusy(false);
  }
}

async function completeMfaSetup({
  mfaUser,
  mfaCode,
  isTotpResetFlow,
  setError,
  setStage,
  setBusy,
}) {
  if (!mfaUser) {
    setError("Session expired. Please sign in again to complete MFA setup.");
    setStage("EMAIL");
    return;
  }

  const code = (mfaCode || "").trim();
  if (!code) {
    setError("Please enter the 6-digit code from your authenticator app.");
    return;
  }

  setBusy(true);
  setError("");

  try {
    await Auth.verifyTotpToken(mfaUser, code);
    await Auth.setPreferredMFA(mfaUser, "TOTP");

    if (isTotpResetFlow) {
      await markTotpResetCompleted();
    } else {
      await markMfaEnrollmentCompleted();
    }

    window.location.replace("/");
  } catch (e) {
    console.error("Error completing MFA setup", e);
    setError(e?.message || "We couldn’t verify your code. Please try again.");
  } finally {
    setBusy(false);
  }
}

async function completeMfaChallenge({
  mfaUser,
  mfaCode,
  setError,
  setStage,
  setBusy,
}) {
  if (!mfaUser) {
    setError(
      "Session expired. Please sign in again to complete MFA verification."
    );
    setStage("EMAIL");
    return;
  }

  const code = (mfaCode || "").trim();
  if (!code) {
    setError("Please enter the 6-digit code from your authenticator app.");
    return;
  }

  setBusy(true);
  setError("");

  try {
    await Auth.confirmSignIn(mfaUser, code, "SOFTWARE_TOKEN_MFA");
    window.location.replace("/");
  } catch (e) {
    console.error("Error confirming MFA challenge", e);
    setError(e?.message || "We couldn’t verify your code. Please try again.");
  } finally {
    setBusy(false);
  }
}

async function completeNewPassword({
  userForNewPw,
  newPw1,
  allOk,
  matchOk,
  authStatus,
  email,
  setError,
  setStage,
  setBusy,
  setMfaUser,
  setOtpAuthUrl,
}) {
  if (!userForNewPw) {
    setError(
      "Session expired. Please sign in again with your temporary password."
    );
    setStage("PASSWORD");
    return;
  }

  if (!allOk) {
    setError("Your password does not meet the requirements.");
    return;
  }

  if (!matchOk) {
    setError("Passwords must match.");
    return;
  }

  setBusy(true);
  setError("");

  try {
    const user = await Auth.completeNewPassword(userForNewPw, newPw1, {});
    const challenge = user?.challengeName;

    if (challenge === "MFA_SETUP") {
      await beginMfaSetupFromFreshUser({
        user,
        email,
        setBusy,
        setError,
        setMfaUser,
        setOtpAuthUrl,
        setStage,
      });
      return;
    }

    if (challenge === "SOFTWARE_TOKEN_MFA") {
      setMfaUser(user);
      setStage("MFA_CHALLENGE");
      return;
    }

    const mfaEnrollmentRequired = !!authStatus?.mfaEnrollmentRequired;
    const mfaEnrollmentCompleted = !!authStatus?.mfaEnrollmentCompleted;
    const hasSoftwareTokenMfa = !!authStatus?.hasSoftwareTokenMfa;

    if (
      mfaEnrollmentRequired &&
      !mfaEnrollmentCompleted &&
      !hasSoftwareTokenMfa
    ) {
      await beginMfaSetupFromFreshUser({
        user,
        email,
        setBusy,
        setError,
        setMfaUser,
        setOtpAuthUrl,
        setStage,
      });
      return;
    }

    window.location.replace("/");
  } catch (e) {
    setError(e?.message || "Could not set your new password. Please try again.");
  } finally {
    setBusy(false);
  }
}

export default function LoginChooser() {
  const history = useHistory();
  const location = useLocation();

  const [stage, setStage] = useState("EMAIL");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [newPw1, setNewPw1] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [userForNewPw, setUserForNewPw] = useState(null);

  const [showNewPw1, setShowNewPw1] = useState(false);
  const [showNewPw2, setShowNewPw2] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const passwordRef = useRef(null);

  const [authStatus, setAuthStatus] = useState(null);

  const [mfaUser, setMfaUser] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [otpAuthUrl, setOtpAuthUrl] = useState("");

  const recoveryToken = getRecoveryTokenFromLocation(location);
  const isTotpResetFlow = isTotpResetResume(location);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const fromQS = (sp.get("email") || "").trim();
    const fromState = ((location.state && location.state.email) || "").trim();

    const fromLS =
      (!fromState &&
        !fromQS &&
        typeof localStorage !== "undefined" &&
        localStorage.getItem(LS_KEY)) ||
      "";

    setEmail(fromState || fromQS || fromLS || "");
  }, [location.search, location.state]);

  useEffect(() => {
    if (stage === "PASSWORD" && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [stage]);

  async function continueOrReset() {
    if (!email) return;

    setBusy(true);
    setError("");
    rememberEmail(LS_KEY, email);

    try {
      const params = new URLSearchParams();
      params.set("username", email);

      if (recoveryToken) {
        params.set("recoveryToken", recoveryToken);
      }

      const response = await fetch(
        `${AUTH_STATUS_BASE}/auth/status?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      const data = await response.json().catch(() => ({}));
      setAuthStatus(data);

      const reset = !!data.reset;
      const totpResetEligible = !!data?.totpResetEligible;
      const shouldShowRecoveryLinkError = !!recoveryToken;

      if (
        shouldShowRecoveryLinkError &&
        data?.mfaRecoveryLinkAlreadyUsed
      ) {
        setError(
          "This recovery link has already been used. Please contact support or an administrator for a new recovery link."
        );
      } else if (
        shouldShowRecoveryLinkError &&
        data?.mfaRecoveryLinkExpired
      ) {
        setError(
          "This recovery link has expired. Please contact support or an administrator for a new recovery link."
        );
      } else if (
        shouldShowRecoveryLinkError &&
        data?.mfaRecoveryLinkInvalid
      ) {
        setError(
          "This recovery link is no longer valid. Please contact support or an administrator for a new recovery link."
        );
      } else if (reset) {
        goForgotCustom(history, email);
      } else if (totpResetEligible && !isTotpResetFlow) {
        goRecoveryCustom(history, email, recoveryToken);
      } else {
        setStage("PASSWORD");
      }
    } catch {
      setAuthStatus(null);
      setStage("PASSWORD");
    } finally {
      setBusy(false);
    }
  }

  async function signInNative() {
    if (!email || !password) return;

    setBusy(true);
    setError("");
    setMfaUser(null);
    setMfaCode("");
    setOtpAuthUrl("");
    rememberEmail(LS_KEY, email);

    try {
      const user = await Auth.signIn(email.trim(), password);
      const challenge = user?.challengeName;

      if (challenge === "NEW_PASSWORD_REQUIRED") {
        setUserForNewPw(user);
        setStage("NEW_PASSWORD");
        return;
      }

      if (challenge === "MFA_SETUP") {
        await beginMfaSetupFromFreshUser({
          user,
          email,
          setBusy,
          setError,
          setMfaUser,
          setOtpAuthUrl,
          setStage,
        });
        return;
      }

      if (challenge === "SOFTWARE_TOKEN_MFA") {
        if (isTotpResetFlow) {
          await beginMfaSetupFromFreshUser({
            user,
            email,
            setBusy,
            setError,
            setMfaUser,
            setOtpAuthUrl,
            setStage,
          });
          return;
        }

        setMfaUser(user);
        setStage("MFA_CHALLENGE");
        return;
      }

      const mfaEnrollmentRequired = !!authStatus?.mfaEnrollmentRequired;
      const mfaEnrollmentCompleted = !!authStatus?.mfaEnrollmentCompleted;
      const hasSoftwareTokenMfa = !!authStatus?.hasSoftwareTokenMfa;

      if (
        mfaEnrollmentRequired &&
        !mfaEnrollmentCompleted &&
        !hasSoftwareTokenMfa
      ) {
        await beginMfaSetupFromFreshUser({
          user,
          email,
          setBusy,
          setError,
          setMfaUser,
          setOtpAuthUrl,
          setStage,
        });
        return;
      }

      window.location.replace("/");
    } catch (e) {
      if (
        e?.challengeName === "NEW_PASSWORD_REQUIRED" ||
        /NEW_PASSWORD_REQUIRED/i.test(e?.message || "")
      ) {
        setUserForNewPw(e);
        setStage("NEW_PASSWORD");
        setBusy(false);
        return;
      }

      if (e?.challengeName === "SOFTWARE_TOKEN_MFA") {
        if (isTotpResetFlow) {
          await beginMfaSetupFromFreshUser({
            user: e,
            email,
            setBusy,
            setError,
            setMfaUser,
            setOtpAuthUrl,
            setStage,
          });

          setBusy(false);
          return;
        }

        setMfaUser(e);
        setStage("MFA_CHALLENGE");
        setBusy(false);
        return;
      }

      if (e?.challengeName === "MFA_SETUP") {
        await beginMfaSetupFromFreshUser({
          user: e,
          email,
          setBusy,
          setError,
          setMfaUser,
          setOtpAuthUrl,
          setStage,
        });

        setBusy(false);
        return;
      }

      const code = e?.code || e?.name || "";
      const msg = (e?.message || "").toLowerCase();

      if (
        code === "PasswordResetRequiredException" ||
        msg.includes("password reset required")
      ) {
        goForgotCustom(history, email);
      } else if (
        code === "InvalidPasswordException" &&
        msg.includes("temporary")
      ) {
        goForgotCustom(history, email);
      } else if (code === "UserNotConfirmedException") {
        setError(
          "Your email isn’t confirmed yet. Please check your inbox for a verification link."
        );
      } else if (
        code === "NotAuthorizedException" ||
        code === "UserNotFoundException"
      ) {
        setError("Invalid email or password.");
      } else {
        setError(e?.message || "We couldn’t sign you in. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  const { req, allOk, matchOk } = useMemo(() => {
    const length = newPw1.length >= 12;
    const upper = /[A-Z]/.test(newPw1);
    const lower = /[a-z]/.test(newPw1);
    const number = /\d/.test(newPw1);
    const symbol = /[^A-Za-z0-9]/.test(newPw1);

    const reqObj = {
      length,
      upper,
      lower,
      number,
      symbol,
    };

    const allOkVal =
      reqObj.length &&
      reqObj.upper &&
      reqObj.lower &&
      reqObj.number &&
      reqObj.symbol;

    const matchOkVal = newPw1.length > 0 && newPw1 === newPw2;

    return {
      req: reqObj,
      allOk: allOkVal,
      matchOk: matchOkVal,
    };
  }, [newPw1, newPw2]);

  function handleSubmitLocal(e) {
    e.preventDefault();

    if (busy) return;

    if (stage === "EMAIL") {
      continueOrReset();
    } else if (stage === "PASSWORD") {
      signInNative();
    } else if (stage === "NEW_PASSWORD") {
      completeNewPassword({
        userForNewPw,
        newPw1,
        allOk,
        matchOk,
        authStatus,
        email,
        setError,
        setStage,
        setBusy,
        setMfaUser,
        setOtpAuthUrl,
      });
    } else if (stage === "MFA_SETUP") {
      completeMfaSetup({
        mfaUser,
        mfaCode,
        isTotpResetFlow,
        setError,
        setStage,
        setBusy,
      });
    } else if (stage === "MFA_CHALLENGE") {
      completeMfaChallenge({
        mfaUser,
        mfaCode,
        setError,
        setStage,
        setBusy,
      });
    }
  }

  return (
    <div className="ph-auth-wrap">
      <div className="ph-auth-card">
        <h1 className="ph-auth-title">
          {stage === "NEW_PASSWORD"
            ? "Set a new password"
            : "Sign in to your account"}
        </h1>

        <form onSubmit={handleSubmitLocal}>
          <label className="ph-label" htmlFor="ph-email">
            Email Address
          </label>
          <input
            id="ph-email"
            className="ph-field"
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            disabled={busy || stage !== "EMAIL"}
          />

          {stage === "PASSWORD" && (
            <>
              <label className="ph-label" htmlFor="ph-password">
                Password
              </label>
              <input
                id="ph-password"
                className="ph-field"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                ref={passwordRef}
                disabled={busy}
              />
            </>
          )}

          {stage === "NEW_PASSWORD" && (
            <>
              <label className="ph-label" htmlFor="ph-new1">
                New Password
              </label>
              <div className="ph-field-wrap">
                <input
                  id="ph-new1"
                  className={`ph-field ${newPw1 && !allOk ? "invalid" : ""}`}
                  type={showNewPw1 ? "text" : "password"}
                  value={newPw1}
                  onChange={(e) => setNewPw1(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Enter a new password"
                />
                <button
                  type="button"
                  className="ph-toggle"
                  onClick={() => setShowNewPw1((s) => !s)}
                  aria-label={showNewPw1 ? "Hide password" : "Show password"}
                  aria-pressed={showNewPw1}
                  disabled={busy}
                >
                  {showNewPw1 ? "Hide" : "Show"}
                </button>
              </div>

              <ul
                className="ph-req-list"
                aria-live="polite"
                aria-atomic="true"
                style={{ margin: "8px 0 12px" }}
              >
                <ReqItem ok={req.length}>At least 12 characters</ReqItem>
                <ReqItem ok={req.upper}>
                  Contains an uppercase letter (A–Z)
                </ReqItem>
                <ReqItem ok={req.lower}>
                  Contains a lowercase letter (a–z)
                </ReqItem>
                <ReqItem ok={req.number}>Contains a number (0–9)</ReqItem>
                <ReqItem ok={req.symbol}>
                  Contains a symbol (e.g., !@#$%)
                </ReqItem>
              </ul>

              <label className="ph-label" htmlFor="ph-new2">
                Confirm New Password
              </label>
              <div className="ph-field-wrap">
                <input
                  id="ph-new2"
                  className="ph-field"
                  type={showNewPw2 ? "text" : "password"}
                  value={newPw2}
                  onChange={(e) => setNewPw2(e.target.value)}
                  autoComplete="new-password"
                  placeholder="Re-enter new password"
                />
                <button
                  type="button"
                  className="ph-toggle"
                  onClick={() => setShowNewPw2((s) => !s)}
                  aria-label={showNewPw2 ? "Hide password" : "Show password"}
                  aria-pressed={showNewPw2}
                  disabled={busy}
                >
                  {showNewPw2 ? "Hide" : "Show"}
                </button>
              </div>

              <div
                className={`ph-req ${matchOk ? "ok" : "bad"}`}
                aria-live="polite"
                style={{ margin: "6px 0 12px" }}
              >
                <span
                  aria-hidden="true"
                  style={{ display: "inline-block", width: 18 }}
                >
                  {matchOk ? "✓" : "✕"}
                </span>
                Passwords match
              </div>
            </>
          )}

          {stage === "MFA_SETUP" && (
            <>
              <p className="ph-hint" style={{ marginTop: 16 }}>
                Scan this code with your authenticator app.
              </p>

              <div
                className="ph-qr-placeholder"
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: 8,
                  padding: 16,
                  textAlign: "center",
                  marginBottom: 12,
                }}
              >
                {otpAuthUrl ? (
                  <QRCodeCanvas value={otpAuthUrl} size={180} />
                ) : (
                  "Preparing QR code…"
                )}
              </div>

              <label
                className="ph-label"
                htmlFor="ph-mfa-setup-code"
                style={{ marginTop: 12 }}
              >
                6-digit code from your authenticator app
              </label>
              <input
                id="ph-mfa-setup-code"
                className="ph-field"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                disabled={busy}
                autoComplete="one-time-code"
              />
            </>
          )}

          {stage === "MFA_CHALLENGE" && (
            <>
              <label
                className="ph-label"
                htmlFor="ph-mfa-challenge-code"
                style={{ marginTop: 16 }}
              >
                6-digit code from your authenticator app
              </label>
              <input
                id="ph-mfa-challenge-code"
                className="ph-field"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="123456"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                disabled={busy}
                autoComplete="one-time-code"
              />
            </>
          )}

          {error && (
            <div className="ph-error" role="alert">
              {error}
            </div>
          )}

          <button
            className="ph-button"
            type="submit"
            disabled={
              busy ||
              !email ||
              (stage === "PASSWORD" && !password) ||
              (stage === "NEW_PASSWORD" && (!newPw1 || !newPw2)) ||
              (stage === "MFA_SETUP" && !mfaCode) ||
              (stage === "MFA_CHALLENGE" && !mfaCode)
            }
          >
            {busy
              ? stage === "PASSWORD"
                ? "Signing in…"
                : stage === "NEW_PASSWORD"
                ? "Updating…"
                : stage === "MFA_SETUP" || stage === "MFA_CHALLENGE"
                ? "Verifying…"
                : "Checking…"
              : stage === "PASSWORD"
              ? "Sign in"
              : stage === "NEW_PASSWORD"
              ? "Set password"
              : stage === "MFA_SETUP"
              ? "Verify & continue"
              : stage === "MFA_CHALLENGE"
              ? "Verify & sign in"
              : "Continue"}
          </button>
        </form>

        {stage !== "NEW_PASSWORD" && (
          <p className="ph-hint">
            <a
              className="ph-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                goForgotCustom(history, email);
              }}
            >
              Reset your password
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
